/*
 * Copyright (c) 2017, Indigen Solutions. All rights reserved.
 *
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 *
 * This software is the confidential and proprietary information.
 *
 * You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement
 * you entered into.
 */

/**
 *
 */
import { ProductHelper } from "./Product.helper";
import * as moment from "moment";
import { Customer } from "../types/Customer";
import { Quotation } from "../types/Quotation";
import { Item } from "../types/Item";
import { User } from "../models/User.model";
import { dicoGOPCO } from "../data/products/go/dicoPCO";
import { dicoGOPVO } from "../data/products/go/dicoPVO";
import { ProductGO } from "../types/ProductGO";
import { PDFFormHelper } from "./PDFForm.helper";
import { map } from "lodash";
import { PDFHelper } from "./PDF.helper";
import { Document, Base64Document } from "../types/Document";

export class ProductGOHelper extends ProductHelper {
    public defaultValue: ProductGO = {
        beneficiaries: [{}],
    };

    private capitalAmounts = {
        initial: 3400,
        serenity: 4100,
        prestige: 5900,
    };

    private pvoSouscriptionPDF: any = null;
    private adhesionPoitiersPDF: any = null;

    public async init(advisor: User, customer: Customer, quotation: Quotation) {
        this.pvoSouscriptionPDF = new PDFFormHelper();
        this.adhesionPoitiersPDF = new PDFFormHelper();
        await this.tarifsXLS.read(require("../data/products/go/form/tarifs.xls"));
        await this.propositionPDF.read(require("../data/products/go/form/proposition.pdf"));
        await this.adhesionPDF.read(require("../data/products/go/form/adhesion.pdf"));
        await this.pvoSouscriptionPDF.read(require("../data/products/go/form/pvo_souscription.pdf"));
        await this.adhesionPoitiersPDF.read(require("../data/products/go/form/adhesion_poitiers.pdf"));
        await this.readCourrierPDF(advisor);
        super.init(advisor, customer, quotation);
    }

    public getDico() {
        if (this.variant === "PCO") {
            return dicoGOPCO;
        } else {
            return dicoGOPVO;
        }
    }

    // Souscription management

    public showPVOSouscriptionPDF() {
        this.pvoSouscriptionPDF.show(this.getPDFields());
    }

    public downloadPVOSouscriptionPDF() {
        this.pvoSouscriptionPDF.download(this.getPDFields(), `SouscriptionPVO.pdf`);
    }

    public async printPVOSouscription() {
        this.pvoSouscriptionPDF.print(this.getPDFields());
    }

    public async getPVOSouscriptionBase64Document(): Promise<Base64Document> {
        const pdfBase64 = await this.pvoSouscriptionPDF.getBase64(this.getPDFields());
        return {
            base64Content: pdfBase64,
            filename: "SouscriptionPVO.pdf",
        };
    }

    // Adhesion management

    public showAdhesionPDF() {
        if (this.advisor.company.name === "mutuelledepoitiers") {
            this.adhesionPoitiersPDF.show(this.getPDFields());
        } else {
            this.adhesionPDF.show(this.getPDFields());
        }
    }

    public downloadAdhesionPDF(documents: Document[]) {
        if (this.advisor.company.name === "mutuelledepoitiers") {
            this.adhesionPoitiersPDF.download(this.getPDFields(), `Adhesion${this.variant ? this.variant : ""}.pdf`);
        } else {
            this.adhesionPDF.download(this.getPDFields(), `Adhesion${this.variant ? this.variant : ""}.pdf`);
        }
        documents.forEach(async (d) => new PDFHelper().download(d.url, d.label + ".pdf"));
    }

    public async sendAdhesionPDF(
        subject: string,
        message: string,
        base64documents: Base64Document[],
        documents: Document[]
    ) {
        let pdfBase64 = null;
        if (this.advisor.company.name === "mutuelledepoitiers") {
            pdfBase64 = await this.adhesionPoitiersPDF.getBase64(this.getPDFields());
        } else {
            pdfBase64 = await this.adhesionPDF.getBase64(this.getPDFields());
        }
        base64documents = base64documents || [];
        base64documents.push({
            base64Content: pdfBase64,
            filename: `Adhesion${this.variant ? this.variant : ""}.pdf`,
        });
        const result = this.send(subject, message, base64documents, documents);
        return result;
    }

    // Utils

    public getPCOGuaranteedCapitalAmountItems(): Item[] {
        this.tarifsXLS.setActiveSheet("5 ans");
        return map(this.getItems("B3:AO3", true), (item) => {
            return {
                name: item.name,
                label: item.label.replace(",", " "),
            };
        });
    }

    public getPCOContribution(): number {
        return this.product.contributionMethod === "unique"
            ? this.getPCOUniqueContribution()
            : this.getPCOMonthlyContribution();
    }

    public getPVOContribution(): number {
        return this.product.contributionMethod === "unique"
            ? this.getPVOUniqueContribution()
            : this.getPVOMonthlyContribution();
    }

    public getPeriodicContribution() {
        if (this.product.contributionMethod === "unique") {
            return this.round(this.product.contribution);
        }
        switch (this.product.samplingInterval) {
            case "month":
                return this.round(this.product.contribution);
            case "trimester":
                return this.round(this.product.contribution) * 3;
            case "year":
                return this.round(this.product.contribution) * 12;
            default:
                return null;
        }
    }

    public async readCourrierPDF(advisor: User) {
        if (advisor.company.name === "ag2r") {
            await this.courrierAdhesionPDF.read(
                require("../data/products/common/form/courrier_accompagnement_adhesion_GPI_GO.pdf")
            );
            await this.courrierPropositionPDF.read(
                require("../data/products/common/form/courrier_accompagnement_proposition_GPI_GO.pdf")
            );
        } else if (advisor.company.name === "viasante") {
            await this.courrierAdhesionPDF.read(
                require("../data/products/common/form/courrier_accompagnement_adhesion_GPI_GO_viasante.pdf")
            );
            await this.courrierPropositionPDF.read(
                require("../data/products/common/form/courrier_accompagnement_proposition_GPI_GO_viasante.pdf")
            );
        } else if (advisor.company.name === "mutuelledepoitiers") {
            await this.courrierAdhesionPDF.read(
                require("../data/products/go/form/courrier_accompagnement_adhesion_mutuelledepoitiers.pdf")
            );
            await this.courrierPropositionPDF.read(
                require("../data/products/go/form/courrier_accompagnement_proposition_mutuelledepoitiers.pdf")
            );
        }
    }

    protected getPDFields() {
        return {
            ...this.getAdvisorPDFFields(),
            ...this.getCustomerPDFFields(),

            // Miss to Mrs
            customer_mrs: this.customer.title === "mrs" || this.customer.title === "miss",

            // Date d'effet souhaitée

            effectiveDate: moment(this.quotation.effectiveDate).format("MMYYYY"),
            effectiveDateFull: moment(this.quotation.effectiveDate).format("DDMMYYYY"),
            // "age": this.getAge(),
            age: this.getAgeAtDate(this.customer.birthdate, this.quotation.effectiveDate).toString(),

            // Mode de prélèvement

            product_contributionMethod_lifetime: this.product.contributionMethod === "lifetime",
            product_contributionMethod_temporary: this.product.contributionMethod === "temporary",
            product_contributionMethod_unique: this.product.contributionMethod === "unique",
            product_contributionTemporaryValue:
                this.product.contributionMethod === "temporary" ? this.product.contributionTemporaryValue : "",
            product_samplingInterval_month: this.product.samplingInterval === "month",
            product_samplingInterval_trimester: this.product.samplingInterval === "trimester",
            product_samplingInterval_year: this.product.samplingInterval === "year",

            // Garanties

            product_type_pco: this.product.type === "pco",
            product_type_pvo: this.product.type === "pvo",

            product_pcoGuaranteedCapitalAmount:
                this.product.type === "pco" ? this.product.pcoGuaranteedCapitalAmount : "",

            product_pvoFormula_initial: this.product.type === "pvo" && this.product.pvoFormula === "initial",
            product_pvoFormula_serenity: this.product.type === "pvo" && this.product.pvoFormula === "serenity",
            product_pvoFormula_prestige: this.product.type === "pvo" && this.product.pvoFormula === "prestige",
            product_pvoOperator: this.product.type === "pvo",

            // Cotisations

            product_periodicContribution: this.getPeriodicContribution(),

            // Beneficiaries

            ...this.getProductBeneficiariesPDFFields(),
        };
    }

    // Override Date

    public getAgeAtDate(birthdate: string, date: string): number {
        return moment(date)
            .date(1)
            .diff(moment(birthdate).date(1), "year");
    }

    private getPCOUniqueContribution(): number {
        this.tarifsXLS.setActiveSheet("PU"); // Prime Unique
        return this.getResult(this.product.pcoGuaranteedCapitalAmount);
    }

    private getPVOUniqueContribution(): number {
        this.tarifsXLS.setActiveSheet("PU"); // Prime Unique
        return this.getResult(this.capitalAmounts[this.product.pvoFormula]);
    }

    private getPCOMonthlyContribution(): number {
        this.selectActiveSheet(this.product.contributionMethod, this.product.contributionTemporaryValue);
        return this.getResult(this.product.pcoGuaranteedCapitalAmount) + 1; // Option assistance obligatoire de 1 €
    }

    private getPVOMonthlyContribution(): number {
        this.selectActiveSheet(this.product.contributionMethod, this.product.contributionTemporaryValue);
        return this.getResult(this.capitalAmounts[this.product.pvoFormula]) + 1; // Option assistance obligatoire de 1 €
    }

    private selectActiveSheet(contributionMethod: string, contributionTemporaryValue: string) {
        switch (contributionMethod) {
            case "lifetime":
                this.tarifsXLS.setActiveSheet("Viager");
                break;
            case "temporary":
                this.tarifsXLS.setActiveSheet(contributionTemporaryValue + " ans");
                break;
            default:
                break;
        }
    }

    private getResult(amount: number) {
        const result = this.tarifsXLS.findProductInRanges(
            "B3:AO3",
            amount,
            "A4:A71",
            this.getAgeAtEffectiveDate() + " ans"
        ); // TODO ?
        if (!result) {
            throw new Error("L'âge de l'adhérent ne correspond pas aux options choisies");
        } else {
            return result.v;
        }
    }
}



// WEBPACK FOOTER //
// ./src/helper/ProductGO.helper.ts